import { Person } from "@mui/icons-material";
import { Grid, Stack, Typography } from "@mui/material";
import { useLookup } from "context/lookupContext";
import CustomSimpleAccordion from "qubCommon/Accordions/CustomSimpleAccordion";
import { ColumnLabel } from "qubCommon/Inputs";
import FormCheckbox from "qubCommon/Inputs/Checkbox";
import ClientAutocomplete from "qubCommon/Inputs/ClientAutocomplete";
import Dropdown from "qubCommon/Inputs/Dropdown";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import TextInput from "src/qubCommon/Inputs/TextInput";
import { EmailVerificationPanel } from "../../ApplicationDetails/Components/MothersInfo";

const MOBILE_PHONE_ERROR_MESSAGE =
  "Το κινητό τηλέφωνο πρέπει να ξεκινά από 69 και να αποτελείται από 10 ψηφία.";

const normalizeMobilePhone = (value) => String(value ?? "").replace(/\s+/g, "");
const isValidMobilePhone = (value) => /^69\d{8}$/.test(normalizeMobilePhone(value));
const getLookupId = (option) => option?.id ?? option?.Id ?? null;
const getLookupName = (option) => option?.name ?? option?.Name ?? "";

const withSelectedOption = (options, selectedOption) => {
  if (!getLookupId(selectedOption)) return options;
  if (
    options.some(
      (option) => String(getLookupId(option)) === String(getLookupId(selectedOption))
    )
  ) {
    return options;
  }

  return [...options, selectedOption];
};

const MothersInfo = ({
  applicant,
  canEdit = false,
  canPartiallyEdit,
  useLegacyPhoneFields = false,
  isLoadedEmailVerified,
  shouldShowEmailVerification,
  forceEmailVerification,
  onEmailVerified,
}) => {
  const { lookups } = useLookup();
  const form = useFormContext();
  const setFormValue = form.setValue;
  const [mobilePhoneError, setMobilePhoneError] = useState("");
  const loadedSingleParentOption = useMemo(
    () => applicant?.singleParentOption || applicant?.SingleParentOption,
    [applicant?.SingleParentOption, applicant?.singleParentOption]
  );
  const singleParentOptions = useMemo(
    () =>
      withSelectedOption(
        lookups?.singleParentOptions || [],
        loadedSingleParentOption
      ),
    [loadedSingleParentOption, lookups?.singleParentOptions]
  );
  const isMotherSingleParent =
    useWatch({
      control: form.control,
      name: "applicant.isMotherSingleParent",
    }) === true;
  const selectedSingleParentOptionId = useWatch({
    control: form.control,
    name: "applicant.singleParentOption.id",
  });
  const applicantSingleParentOptionId = getLookupId(loadedSingleParentOption);
  const effectiveSingleParentOptionId =
    selectedSingleParentOptionId === undefined
      ? applicantSingleParentOptionId
      : selectedSingleParentOptionId;

  const syncSingleParentOption = useCallback(
    (option, setValueOptions = {}) => {
      form.setValue(
        "applicant.singleParentOption",
        option
          ? {
            id: getLookupId(option),
            name: getLookupName(option),
          }
          : null,
        setValueOptions
      );
    },
    [form]
  );

  const handleMobilePhoneBlur = useCallback(
    (event) => {
      const normalizedValue = normalizeMobilePhone(event.target.value);

      if (normalizedValue !== event.target.value) {
        event.target.value = normalizedValue;
        form.setValue("applicant.mobilePhone", normalizedValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      }

      setMobilePhoneError(
        isValidMobilePhone(normalizedValue) ? "" : MOBILE_PHONE_ERROR_MESSAGE
      );
    },
    [form]
  );

  useEffect(() => {
    if (useLegacyPhoneFields) setMobilePhoneError("");
  }, [useLegacyPhoneFields]);

  useEffect(() => {
    if (!applicant?.address?.municipality) {
      setFormValue("applicant.address.municipality", null);
    }
  }, [applicant?.address?.municipality, setFormValue]);

  useEffect(() => {
    if (!canEdit && !canPartiallyEdit) return;

    const setValueOptions = {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    };

    if (!isMotherSingleParent || !effectiveSingleParentOptionId) {
      syncSingleParentOption(null, setValueOptions);
      return;
    }

    const selectedOption = singleParentOptions.find(
      (option) =>
        String(getLookupId(option)) === String(effectiveSingleParentOptionId)
    );

    syncSingleParentOption(selectedOption || loadedSingleParentOption, setValueOptions);
  }, [
    canEdit,
    canPartiallyEdit,
    effectiveSingleParentOptionId,
    isMotherSingleParent,
    loadedSingleParentOption,
    singleParentOptions,
    syncSingleParentOption,
  ]);

  const AccordionTitle = () => (
    <Stack direction="row" gap={1}>
      <Person color="primary" fontSize="small" sx={{ marginLeft: "2px" }} />
      <Typography color="primary" sx={{ fontWeight: 1000 }}>
        ΣΤΟΙΧΕΙΑ ΑΙΤΟΥΣΑΣ/ΑΙΤΟΥΝΤΟΣ
      </Typography>
      <Typography color="primary" sx={{ marginLeft: "18px", fontWeight: 500 }}>
        {applicant?.lastName} {applicant?.firstName}
      </Typography>
    </Stack>
  );

  const AccordionContent = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.ama" label="Α.Μ.ΟΓΑ/ΕΦΚΑ*" />
      </Grid>
      <Grid item xs={12} sm={2}>
        <Stack direction="column" gap={1}>
          <ColumnLabel label="Οικογένεια" />
          <FormCheckbox
            name="applicant.isMotherSingleParent"
            label="Μονογονεϊκή"
            disabled={!canEdit}
          />
        </Stack>
      </Grid>
      {isMotherSingleParent && (
        <Grid item xs={12} sm={4}>
          <Dropdown
            name="applicant.singleParentOption.id"
            label="Επιλογή μονογονεϊκής οικογένειας"
            valueName="id"
            labelName="name"
            options={singleParentOptions}
            defaultValue={applicantSingleParentOptionId}
            disabled={!canEdit}
            changeFn={(event) => {
              const selectedOption = singleParentOptions.find(
                (option) =>
                  String(getLookupId(option)) === String(event.target.value)
              );
              syncSingleParentOption(selectedOption || null);
            }}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.amka" label="AMKA*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.afm" label="ΑΦΜ*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.firstName" label="Όνομα*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.lastName" label="Επώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.fatherName" label="Πατρώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput disabled name="applicant.motherName" label="Μητρώνυμο*" />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.phone1"
          label="Τηλέφωνο 1*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name={useLegacyPhoneFields ? "applicant.phone2" : "applicant.mobilePhone"}
          label={
            useLegacyPhoneFields
              ? "Τηλέφωνο 2"
              : "Κινητό Τηλέφωνο* (69XXXXXXXX)"
          }
          blurFn={useLegacyPhoneFields ? undefined : handleMobilePhoneBlur}
          error={!useLegacyPhoneFields && !!mobilePhoneError}
          helperText={!useLegacyPhoneFields ? mobilePhoneError : undefined}
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Stack spacing={0.75}>
          <TextInput
            name="applicant.email"
            label="Email*"
            disabled={!canEdit && !canPartiallyEdit}
          />
          <EmailVerificationPanel
            applicant={applicant}
            canEdit={canEdit}
            canPartiallyEdit={canPartiallyEdit}
            isLoadedEmailVerified={isLoadedEmailVerified}
            shouldShowVerification={shouldShowEmailVerification}
            forceVisible={forceEmailVerification}
            onVerified={onEmailVerified}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} sm={8} md={8}>
        <TextInput
          name="applicant.address.addressLine"
          label="Διεύθυνση*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={4} md={4} lg={4}>
        <TextInput
          name="applicant.address.postalCode"
          label="ΤΚ*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextInput
          name="applicant.address.city"
          label="Πόλη*"
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <ClientAutocomplete
          name="applicant.address.municipality.id"
          label="Δήμος*"
          valueName="id"
          labelName="name"
          customChangeFn={(value) => {
            if (!value) form.setValue("applicant.address.municipality", null);
          }}
          options={lookups?.municipalities || []}
          defaultValue={applicant?.address?.municipality?.id}
          disabled={!canEdit && !canPartiallyEdit}
        />
      </Grid>
    </Grid>
  );

  return (
    <CustomSimpleAccordion
      title={<AccordionTitle />}
      content={AccordionContent()}
    />
  );
};

export default MothersInfo;
